import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'

export const GAP_SOURCE_OPTIONS = ['PCP Sentinel', 'ADL', 'PCPMO26'] as const

const GAP_EXCLUDED_SOURCES = ['MBFCheck']
export const GAP_PAGE_SIZE = 25

export type GapRow = {
    sourceDb: string
    introducerLabel: string
    caseRef: string
    policyNumber: string
    title: string
    firstName: string
    lastName: string
    dob: string | null
    email: string
    telephone: string
    houseNumber: string
    houseName: string
    flat: string
    street: string
    townCity: string
    county: string
    postcode: string
    addressLineOne: string
    addressLineTwo: string
    ccjs: number | null
    voluntaryArrangements: number | null
    lender: string
    startDate: string | null
    totalAmountPayable: number | null
    startingBalance: number | null
    lengthOfContract: number | null
    monthlyRepayment: number | null
    cfaSignedDate: string | null
    esignDate: string | null
}

export type GapSummary = {
    fundingReferenceRows: number
    introducerClaimsScanned: number
    matchedToFundingMonitor: number
    gapCount: number
}

const distinctNonEmptyEmailCount = (rows: GapRow[]) => {
    const set = new Set<string>()
    for (const row of rows) {
        const e = row.email?.trim().toLowerCase()
        if (e) set.add(e)
    }
    return set.size
}

export type GapFilterOpen = 'search' | 'source' | 'lender' | 'cfa' | 'esign' | 'tel' | 'dob' | 'address' | null

export const useIntroducerFundingGapViewState = () => {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [summary, setSummary] = useState<GapSummary | null>(null)
    const [gaps, setGaps] = useState<GapRow[]>([])
    const [search, setSearch] = useState('')
    /** Empty = all sources (same convention as ineligible explorer). */
    const [selectedSources, setSelectedSources] = useState<string[]>([])
    /** Empty = all lenders. */
    const [selectedLenders, setSelectedLenders] = useState<string[]>([])
    const [lenderSort, setLenderSort] = useState<'alpha' | 'count'>('count')
    const [cfaFilter, setCfaFilter] = useState<'all' | 'present' | 'missing'>('all')
    const [esignFilter, setEsignFilter] = useState<'all' | 'present' | 'missing'>('all')
    const [telFilter, setTelFilter] = useState<'all' | 'present' | 'missing'>('all')
    const [dobFilter, setDobFilter] = useState<'all' | 'present' | 'missing'>('all')
    const [addressFilter, setAddressFilter] = useState<'all' | 'present' | 'missing'>('all')

    const [currentPage, setCurrentPage] = useState(1)

    const [openGapFilter, setOpenGapFilter] = useState<GapFilterOpen>(null)
    const [filterPopoverPosition, setFilterPopoverPosition] = useState<CSSProperties | null>(null)
    const gapFilterBarRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        const load = async () => {
            setLoading(true)
            setError(null)
            try {
                const response = await api({ url: '/introducer-funding-gap', data: {} })
                if (response.data?.success) {
                    setSummary(response.data.summary ?? null)
                    setGaps(Array.isArray(response.data.gaps)
                        ? response.data.gaps.filter((g: GapRow) => !GAP_EXCLUDED_SOURCES.includes(g.sourceDb))
                        : [])
                } else {
                    setError(response.data?.message || 'Failed to load data')
                    setGaps([])
                    setSummary(null)
                }
            } catch (err: any) {
                if (err?.response?.status === 401) {
                    Report.failure('Session Expired', 'Please log in again.', 'OK')
                    navigate('/login')
                    return
                }
                setError(err?.message || 'Request failed')
                setGaps([])
                setSummary(null)
            } finally {
                setLoading(false)
            }
        }
        load()
    }, [navigate])

    useEffect(() => {
        if (!openGapFilter) return
        const handleOutside = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null
            if (target && gapFilterBarRef.current?.contains(target)) return
            setOpenGapFilter(null)
            setFilterPopoverPosition(null)
        }
        document.addEventListener('mousedown', handleOutside)
        document.addEventListener('touchstart', handleOutside)
        return () => {
            document.removeEventListener('mousedown', handleOutside)
            document.removeEventListener('touchstart', handleOutside)
        }
    }, [openGapFilter])

    const sorted = useMemo(
        () =>
            [...gaps].sort((a, b) => {
                const s = a.sourceDb.localeCompare(b.sourceDb)
                if (s !== 0) return s
                return (a.caseRef || '').localeCompare(b.caseRef || '')
            }),
        [gaps]
    )

    const sourceCounts = useMemo(() => {
        const m: Record<string, number> = {}
        for (const row of sorted) {
            m[row.sourceDb] = (m[row.sourceDb] || 0) + 1
        }
        return m
    }, [sorted])

    /** Distinct non-empty lender values, ordered by lenderSort preference. */
    const lenderOptions = useMemo(() => {
        const set = new Set<string>()
        for (const row of sorted) {
            if (row.lender) set.add(row.lender)
        }
        const names = Array.from(set)
        if (lenderSort === 'count') {
            const counts: Record<string, number> = {}
            for (const row of sorted) {
                if (row.lender) counts[row.lender] = (counts[row.lender] || 0) + 1
            }
            return names.sort((a, b) => (counts[b] ?? 0) - (counts[a] ?? 0) || a.localeCompare(b))
        }
        return names.sort((a, b) => a.localeCompare(b))
    }, [sorted, lenderSort])

    /** Per-lender row counts (across all rows, before lender filter, so pills show totals). */
    const lenderCounts = useMemo(() => {
        const m: Record<string, number> = {}
        for (const row of sorted) {
            if (row.lender) m[row.lender] = (m[row.lender] || 0) + 1
        }
        return m
    }, [sorted])

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase()
        return sorted.filter((row) => {
            if (selectedSources.length > 0 && !selectedSources.includes(row.sourceDb)) return false
            if (selectedLenders.length > 0 && !selectedLenders.includes(row.lender)) return false
            if (cfaFilter === 'present' && !row.cfaSignedDate) return false
            if (cfaFilter === 'missing' && row.cfaSignedDate) return false
            if (esignFilter === 'present' && !row.esignDate) return false
            if (esignFilter === 'missing' && row.esignDate) return false
            const hasTel = !!row.telephone?.trim()
            if (telFilter === 'present' && !hasTel) return false
            if (telFilter === 'missing' && hasTel) return false
            const hasDob = !!row.dob
            if (dobFilter === 'present' && !hasDob) return false
            if (dobFilter === 'missing' && hasDob) return false
            const hasAddress = !!(row.addressLineOne?.trim() || row.houseNumber?.trim() || row.street?.trim() || row.postcode?.trim())
            if (addressFilter === 'present' && !hasAddress) return false
            if (addressFilter === 'missing' && hasAddress) return false
            if (!q) return true
            const haystack = [
                row.sourceDb,
                row.introducerLabel,
                row.caseRef,
                row.policyNumber,
                row.firstName,
                row.lastName,
                row.email,
                row.lender,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()
            return haystack.includes(q)
        })
    }, [sorted, search, selectedSources, selectedLenders, cfaFilter, esignFilter, telFilter, dobFilter, addressFilter])

    // Reset to page 1 whenever the filtered set changes
    useEffect(() => { setCurrentPage(1) }, [filteredRows])

    const totalPages = Math.max(1, Math.ceil(filteredRows.length / GAP_PAGE_SIZE))
    const paginatedRows = useMemo(() => {
        const start = (currentPage - 1) * GAP_PAGE_SIZE
        return filteredRows.slice(start, start + GAP_PAGE_SIZE)
    }, [filteredRows, currentPage])

    const handlePageChange = (page: number) => {
        setCurrentPage(page)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    /** Distinct non-empty emails among table rows that pass current search/source filters. */
    const gapDistinctEmails = useMemo(() => distinctNonEmptyEmailCount(filteredRows), [filteredRows])
    const loadedGapDistinctEmails = useMemo(() => distinctNonEmptyEmailCount(sorted), [sorted])

    /** Share of loaded gap rows that still match search/source filters (bar moves when you narrow the table). */
    const gapPctOfTableRows = sorted.length > 0 ? (filteredRows.length / sorted.length) * 100 : 0
    /** Bar: filtered distinct emails vs distinct emails among all gap rows loaded (0% if no rows carry an email). */
    const distinctEmailsPctOfLoadedGap =
        loadedGapDistinctEmails > 0 ? (gapDistinctEmails / loadedGapDistinctEmails) * 100 : 0

    const hasSearch = search.trim().length > 0
    const hasSourceFilter = selectedSources.length > 0
    const hasLenderFilter = selectedLenders.length > 0
    const hasCfaFilter = cfaFilter !== 'all'
    const hasEsignFilter = esignFilter !== 'all'
    const hasTelFilter = telFilter !== 'all'
    const hasDobFilter = dobFilter !== 'all'
    const hasAddressFilter = addressFilter !== 'all'
    const searchSummary =
        search.trim().length > 34 ? `${search.trim().slice(0, 34)}...` : search.trim()
    const sourceFilterSummary =
        selectedSources.length === 1 ? selectedSources[0] : `${selectedSources.length} selected`
    const lenderFilterSummary =
        selectedLenders.length === 1 ? selectedLenders[0] : `${selectedLenders.length} selected`
    const cfaFilterSummary = cfaFilter === 'present' ? 'Has date' : cfaFilter === 'missing' ? 'Missing' : ''
    const esignFilterSummary = esignFilter === 'present' ? 'Has date' : esignFilter === 'missing' ? 'Missing' : ''
    const telFilterSummary = telFilter === 'present' ? 'Has number' : telFilter === 'missing' ? 'Missing' : ''
    const dobFilterSummary = dobFilter === 'present' ? 'Has DOB' : dobFilter === 'missing' ? 'Missing' : ''
    const addressFilterSummary = addressFilter === 'present' ? 'Has address' : addressFilter === 'missing' ? 'Missing' : ''

    const toggleGapFilter = (which: Exclude<GapFilterOpen, null>, event: ReactMouseEvent<HTMLButtonElement>, width = 430) => {
        if (openGapFilter === which) {
            setOpenGapFilter(null)
            setFilterPopoverPosition(null)
            return
        }
        const rect = event.currentTarget.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        const actualWidth = Math.min(width, viewportWidth - 24)
        const left = Math.max(12, Math.min(rect.left + rect.width / 2 - actualWidth / 2, viewportWidth - actualWidth - 12))
        setFilterPopoverPosition({
            top: rect.bottom + 8,
            left,
            width: actualWidth,
        })
        setOpenGapFilter(which)
    }

    const toggleSource = (name: string) => {
        setSelectedSources((prev) => (prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]))
    }

    const resetSourceFilter = () => setSelectedSources([])

    const toggleLender = (name: string) => {
        setSelectedLenders((prev) => (prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]))
    }

    const resetLenderFilter = () => setSelectedLenders([])

    const resetCfaFilter = () => setCfaFilter('all')
    const resetEsignFilter = () => setEsignFilter('all')
    const resetTelFilter = () => setTelFilter('all')
    const resetDobFilter = () => setDobFilter('all')
    const resetAddressFilter = () => setAddressFilter('all')

    const exportGapTableCsv = useCallback(() => {
        const headers = [
            'Source',
            'Row introducer label',
            'Case ref',
            'Policy number',
            'Title',
            'First name',
            'Last name',
            'DOB',
            'Email',
            'Telephone',
            'House number',
            'House name',
            'Flat',
            'Street',
            'Town / city',
            'County',
            'Postcode',
            'Address line 1',
            'Address line 2',
            'CCJs',
            'Voluntary arrangements',
            'Lender',
            'Start date',
            'Total amount payable',
            'Starting balance',
            'Length of contract (months)',
            'Monthly repayment',
            'CFA signed date',
            'E-sign date',
        ] as const
        const escapeCell = (raw: string) => {
            const s = String(raw ?? '').replace(/\r\n|\n|\r/g, ' ')
            if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
            return s
        }
        const lines = [
            headers.join(','),
            ...filteredRows.map((row) =>
                [
                    escapeCell(row.sourceDb),
                    escapeCell(row.introducerLabel),
                    escapeCell(row.caseRef),
                    escapeCell(row.policyNumber),
                    escapeCell(row.title),
                    escapeCell(row.firstName),
                    escapeCell(row.lastName),
                    escapeCell(row.dob ?? ''),
                    escapeCell(row.email),
                    escapeCell(row.telephone),
                    escapeCell(row.houseNumber),
                    escapeCell(row.houseName),
                    escapeCell(row.flat),
                    escapeCell(row.street),
                    escapeCell(row.townCity),
                    escapeCell(row.county),
                    escapeCell(row.postcode),
                    escapeCell(row.addressLineOne),
                    escapeCell(row.addressLineTwo),
                    escapeCell(row.ccjs != null ? String(row.ccjs) : ''),
                    escapeCell(row.voluntaryArrangements != null ? String(row.voluntaryArrangements) : ''),
                    escapeCell(row.lender),
                    escapeCell(row.startDate ?? ''),
                    escapeCell(row.totalAmountPayable != null ? String(row.totalAmountPayable) : ''),
                    escapeCell(row.startingBalance != null ? String(row.startingBalance) : ''),
                    escapeCell(row.lengthOfContract != null ? String(row.lengthOfContract) : ''),
                    escapeCell(row.monthlyRepayment != null ? String(row.monthlyRepayment) : ''),
                    escapeCell(row.cfaSignedDate ?? ''),
                    escapeCell(row.esignDate ?? ''),
                ].join(','),
            ),
        ]
        const csv = `\uFEFF${lines.join('\n')}`
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `introducer-funding-gap-${new Date().toISOString().slice(0, 10)}.csv`
        a.click()
        URL.revokeObjectURL(url)
    }, [filteredRows])

    return {
        loading,
        error,
        summary,
        search,
        setSearch,
        selectedSources,
        selectedLenders,
        openGapFilter,
        filterPopoverPosition,
        gapFilterBarRef,
        sorted,
        sourceCounts,
        lenderOptions,
        lenderCounts,
        lenderSort,
        setLenderSort,
        filteredRows,
        paginatedRows,
        currentPage,
        totalPages,
        handlePageChange,
        gapDistinctEmails,
        loadedGapDistinctEmails,
        gapPctOfTableRows,
        distinctEmailsPctOfLoadedGap,
        hasSearch,
        hasSourceFilter,
        hasLenderFilter,
        hasCfaFilter,
        hasEsignFilter,
        hasTelFilter,
        hasDobFilter,
        hasAddressFilter,
        searchSummary,
        sourceFilterSummary,
        lenderFilterSummary,
        cfaFilterSummary,
        esignFilterSummary,
        telFilterSummary,
        dobFilterSummary,
        addressFilterSummary,
        cfaFilter,
        setCfaFilter,
        esignFilter,
        setEsignFilter,
        telFilter,
        setTelFilter,
        dobFilter,
        setDobFilter,
        addressFilter,
        setAddressFilter,
        toggleGapFilter,
        toggleSource,
        resetSourceFilter,
        toggleLender,
        resetLenderFilter,
        resetCfaFilter,
        resetEsignFilter,
        resetTelFilter,
        resetDobFilter,
        resetAddressFilter,
        exportGapTableCsv,
    }
}
